import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, X, Wifi, WifiOff, Loader } from 'lucide-react';

const API_BASE = window.location.origin;

const playNotificationSound = () => {
  try {
    const context = new (window.AudioContext || window.webkitAudioContext)();
    if (!context) return;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(900, context.currentTime);
    gain.gain.setValueAtTime(0.1, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.00001, context.currentTime + 0.5);
    oscillator.start(context.currentTime);
    oscillator.stop(context.currentTime + 0.4);
  } catch (e) {
    console.error("Could not play sound:", e);
  }
};

const ChatBox = ({ currentUser, allUsers = [] }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [status, setStatus] = useState('connecting');
  const [hasUnread, setHasUnread] = useState(false);
  const [hasMention, setHasMention] = useState(false);
  
  const [isMentioning, setIsMentioning] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [filteredMentions, setFilteredMentions] = useState([]);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingTimeoutRef = useRef();
  const componentIsMounted = useRef(true);
  const lastTimestampRef = useRef(0);
  const originalDocTitle = useRef(document.title);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });

  const longPoll = useCallback(async () => {
    if (!componentIsMounted.current) return;
    clearTimeout(pollingTimeoutRef.current);

    try {
      const response = await fetch(`${API_BASE}/api.php?action=getChatMessages&timestamp=${lastTimestampRef.current}`);
      if (!componentIsMounted.current) return;

      if (response.status === 200) {
        const data = await response.json();
        lastTimestampRef.current = data?.timestamp || lastTimestampRef.current;
        
        if (data?.messages?.length > 0) {
          const newMessagesFromServer = data.messages;
          
          setMessages(prevMessages => {
            const existingIds = new Set(prevMessages.map(m => m.id));
            const trulyNewMessages = newMessagesFromServer.filter(nm => !existingIds.has(nm.id));
            if (trulyNewMessages.length === 0) return prevMessages;
            return [...prevMessages, ...trulyNewMessages];
          });

          const isMentioned = newMessagesFromServer.some(msg => msg.author !== currentUser && msg.mentions?.includes(currentUser));
          
          setIsOpen(currentIsOpen => {
              if (!currentIsOpen) {
                  setHasUnread(true);
                  if (isMentioned) {
                      setHasMention(true);
                      playNotificationSound();
                      document.title = "(!) New Mention!";
                  }
              }
              return currentIsOpen;
          });
        }
        setStatus('connected');
        pollingTimeoutRef.current = setTimeout(longPoll, 100);
      } else if (response.status === 304) {
        pollingTimeoutRef.current = setTimeout(longPoll, 100);
      } else { throw new Error(`Server responded with status ${response.status}`); }
    } catch (error) {
      if (!componentIsMounted.current) return;
      setStatus('disconnected');
      pollingTimeoutRef.current = setTimeout(longPoll, 5000);
    }
  }, [currentUser]);

  useEffect(() => {
    componentIsMounted.current = true;
    fetch(`${API_BASE}/api.php?action=getChatMessages`)
      .then(res => res.json())
      .then(data => {
        if(componentIsMounted.current) {
          setMessages(data.messages || []);
          lastTimestampRef.current = data.timestamp || 0;
          setStatus('connected');
          longPoll();
        }
      }).catch(() => setStatus('disconnected'));

    return () => {
      componentIsMounted.current = false;
      clearTimeout(pollingTimeoutRef.current);
      document.title = originalDocTitle.current;
    };
  }, [longPoll]);

  useEffect(() => { if (isOpen) scrollToBottom(); }, [messages, isOpen]);

  const handleToggleChat = () => {
    setIsOpen(prev => {
      if (!prev) {
        setHasUnread(false);
        setHasMention(false);
        document.title = originalDocTitle.current;
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      return !prev;
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === '' || !currentUser) return;

    const mentionedUsers = [...new Set(newMessage.match(/@(\w+)/g)?.map(m => m.substring(1)) || [])];
    const messageToSend = newMessage.trim();

    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      id: tempId,
      author: currentUser,
      content: messageToSend,
      timestamp: new Date().toISOString(),
      mentions: mentionedUsers,
    };
    
    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage('');
    setIsMentioning(false);
    setTimeout(scrollToBottom, 0);

    try {
      const response = await fetch(`${API_BASE}/api.php?action=sendChatMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ author: currentUser, message: messageToSend, mentions: mentionedUsers }),
      });
      const result = await response.json();
      if(result.success && result.message) {
        // Replace optimistic message with final one from server
        setMessages(prev => prev.map(m => m.id === tempId ? result.message : m));
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (err) {
      console.error("Send error:", err);
      setStatus('disconnected');
      setMessages(prev => prev.filter(m => m.id !== tempId)); // Rollback on error
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    const mentionMatch = value.match(/@(\w*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1].toLowerCase();
      setIsMentioning(true);
      setFilteredMentions(allUsers.filter(u => u.toLowerCase().startsWith(query) && u !== currentUser));
      setMentionIndex(0);
    } else {
      setIsMentioning(false);
    }
  };
  
  const handleMentionSelect = (name) => {
    const atIndex = newMessage.lastIndexOf('@');
    const newMsg = newMessage.substring(0, atIndex) + `@${name} `;
    setNewMessage(newMsg);
    setIsMentioning(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (isMentioning && filteredMentions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex(i => (i + 1) % filteredMentions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex(i => (i - 1 + filteredMentions.length) % filteredMentions.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleMentionSelect(filteredMentions[mentionIndex]);
      }
    }
  };

  const renderMessageContent = (content) => {
    const parts = content.split(/(@\w+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@')) {
        const user = part.substring(1);
        if (allUsers.includes(user)) {
          return <strong key={i} className="mention-span">{part}</strong>;
        }
      }
      return part;
    });
  };
  
  if (!isOpen) {
    return (
      <div className={`chat-fab ${hasMention ? 'has-mention' : ''}`} onClick={handleToggleChat} title="Open Team Chat">
        {hasUnread && <div className="chat-unread-indicator" />}
        <MessageSquare size={28} color="white" />
      </div>
    );
  }

  return (
    <div className="chat-container">
      <div className="chat-header" onClick={handleToggleChat}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={20}/><span>Team Chat</span>
        </div><X size={20}/>
      </div>
      <div className={`chat-status ${status}`}>
        {status === 'connected' ? <Wifi size={14}/> : status === 'disconnected' ? <WifiOff size={14}/> : <Loader size={14} className="animate-spin"/>}
        {status}
      </div>
      <div className="chat-messages" ref={messagesEndRef}>
        {messages.map((msg) => (
          <div key={msg.id} style={{display:'flex', flexDirection:'column', alignItems: msg.author === currentUser ? 'flex-end' : 'flex-start'}}>
            <div className={`chat-message-bubble ${msg.author === currentUser ? 'self' : ''} ${msg.mentions?.includes(currentUser) ? 'mentioned' : ''}`}>
              <strong>{msg.author === currentUser ? 'You' : msg.author}:</strong> {renderMessageContent(msg.content)}
            </div>
            <div className="chat-message-meta">{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        ))}
      </div>
      <form onSubmit={handleSendMessage} className="chat-input-area">
        {isMentioning && filteredMentions.length > 0 && (
          <div className="mention-list">
            {filteredMentions.map((user, idx) => (
              <div key={user} 
                   className={`mention-item ${idx === mentionIndex ? 'selected' : ''}`} 
                   onClick={() => handleMentionSelect(user)}>
                {user}
              </div>
            ))}
          </div>
        )}
        <input ref={inputRef} type="text" className="chat-input" placeholder="Type a message, use @ to mention..."
          value={newMessage} onChange={handleInputChange} onKeyDown={handleKeyDown} disabled={status !== 'connected'}/>
        <button type="submit" className="chat-send-btn" disabled={newMessage.trim() === '' || status !== 'connected'}>
          <Send size={16}/>
        </button>
      </form>
    </div>
  );
};

export default ChatBox;
